import React, {useState} from "react";
import {ChevronRightRegular, ChevronLeftRegular, Info32Regular, Info32Filled} from '@fluentui/react-icons';
import { 
    ToolbarButton, 
    Body1Stronger,
    Dialog,
    DialogSurface,
    DialogTitle,
    DialogBody,
    DialogActions,
    DialogTrigger,
    Button,
    DialogContent,
    Tooltip,
    useToastController,
    Field,
} from "@fluentui/react-components";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import {previousMonday, nextMonday, endOfMonth, getMonth, addDays} from "date-fns";
import {useGlobalStatus} from '../../services/GlobalStatusProvider';
import {GetA23WeekTimeEntries, GetTimesheetData, SubmitTimesheet, GetHolidayCalendar} from '../../services/apiService';
import {AddNewTimecode, setTimesheetEntryStatus, updateTimeSheetData, getLockedMonths} from '../../services/TimesheetDataService';
import './Timesheet.css';
import TooltipWatcherWidget from '../TooltipWatcherWidget';
import EnvConfig from './EnvConfig';
import { showErrorToast, showSuccessToast, TOASTER_ID } from "../../services/toastUtils";
import CasualSubmit from "./CasualMonthlySubmit";
import { DateRangeType, DayOfWeek } from "@fluentui/react-calendar-compat";

export default function WeekSelector(props) {
    const {loading, currentWeekStart, setCurrentWeekStart, lastWeekStart, setLastWeekStart, currentWeekStartDisplay, startLoad, endLoad, isTimesheetSubmitted, markTimesheetSubmitted, 
        markTimesheetUnsubmitted, disableAddTimecode, enableAddTimecode, isDesktop, setTimesheetStatus, useStyles,
        isCasualSubmitLocked, setIsCasualSubmitLocked, lockedMonths, setLockedMonths} = useGlobalStatus();
    const [open, setOpen] = useState(false);
    const styles = useStyles();
    const [infoActive, setInfoActive] = React.useState(false);
    const { dispatchToast } = useToastController(TOASTER_ID);
    const [currentMonthEnd, setCurrentMonthEnd] = useState(endOfMonth(currentWeekStart));


    const handleInfoToggle = () => {
        document.body.classList.contains("show-tooltip") ? document.body.classList.remove('show-tooltip') : document.body.classList.add('show-tooltip');
        setInfoActive(!infoActive);
    }
    
    const handleDateChange = async (change, manualDate = null) => {  
        startLoad();
        let newWeekStart = manualDate || currentWeekStart;
        let oldWeekStart = newWeekStart;
        const previousWeekStart = newWeekStart;
        setLastWeekStart(previousWeekStart);
        
        if (change==='previous') {
            newWeekStart = previousMonday(newWeekStart);
            setCurrentWeekStart(old => previousMonday(old));
            setCurrentMonthEnd(endOfMonth(newWeekStart));
        } 
        else if (change==='next') {
            newWeekStart = nextMonday(newWeekStart);
            setCurrentWeekStart(old => nextMonday(old));
            setCurrentMonthEnd(endOfMonth(newWeekStart));
        }
        

        let existingResources = [];
        let oldResources = []
        try {
            existingResources = await GetA23WeekTimeEntries(newWeekStart);
            oldResources = await GetA23WeekTimeEntries(previousWeekStart);

        } catch {
            showErrorToast(dispatchToast, "Failed to get time entries");
        }
        // Set Previous weeks data
           const { 
            updatedData: updatedLastWeekTimesheetData, 
            isLocked: lastWeekLocked 
            } = await updateTimeSheetData(oldResources, previousWeekStart);
            
        props.setPrevWeekTimesheetData(updatedLastWeekTimesheetData)

        let updatedTimesheetData = [];
        // Only update the locked months if the month is new        
        let currentLockedMonths = lockedMonths
        if(oldWeekStart.getMonth() !== addDays(newWeekStart, 6).getMonth()){
             currentLockedMonths = getLockedMonths(existingResources, currentLockedMonths)
            setLockedMonths(currentLockedMonths);
        }
        let casualSubmitLocked = false;
        existingResources.forEach(resource => {
            if (resource.timeEntries.length > 0) {
                resource.timeEntries.forEach(entry => {
                    if (entry.casualMonthLocked && new Date(entry.date) <= endOfMonth(newWeekStart)) casualSubmitLocked = true;
                });
                // Add the resource info if it has existing entries for the week
                updatedTimesheetData = AddNewTimecode(
                    newWeekStart,
                    updatedTimesheetData,
                    {clientId :resource.clientId, clientName: resource.clientName},
                    {projectId: resource.projectId, projectName: resource.projectName, projectStart: resource.projectStart, projectEnd: resource.projectEnd},
                    {budgetId: resource.budgetId, budgetName: resource.budgetName},
                    {taskId: resource.taskId, taskName: resource.taskName, taskStartDate: resource.taskStart, taskEndDate: resource.taskEnd},
                    {userStartDate: resource.userMapping.userStart, userEndDate: resource.userMapping.userEnd},
                    resource,
                    currentLockedMonths
                );
            }
        });
        // Need to do it this way otherwise it will stay set to true when cycling weeks if it has been set
        if (casualSubmitLocked) {
            setIsCasualSubmitLocked(true);
        } else {
            setIsCasualSubmitLocked(false);
        }
        // Get timesheet status
        let timesheet = '';
        try {
        timesheet = await GetTimesheetData(newWeekStart);
        } catch {
        showErrorToast(dispatchToast, "Failed to get timesheet status");
        }

        props.setLeaveData(timesheet.leaveDays);
        props.setCalendarData(await GetHolidayCalendar(newWeekStart));

        switch (timesheet.timesheetStatus) {
        case "Submitted":
            setTimesheetEntryStatus(updatedTimesheetData, "closed");
            props.setTimesheetStatusMessage("Timesheet Submitted");
            disableAddTimecode();
            markTimesheetSubmitted();
            setTimesheetStatus(false);
            break;
        case "Rejected":
            setTimesheetEntryStatus(updatedTimesheetData, "rejected");
            props.setTimesheetStatusMessage("Timesheet Rejected!");
            enableAddTimecode();
            markTimesheetUnsubmitted();
            setTimesheetStatus(true);
            break;
        case "Needs Attention":
            setTimesheetEntryStatus(updatedTimesheetData, "rejected");
            props.setTimesheetStatusMessage("Timesheet Rejected!");
            enableAddTimecode();
            markTimesheetUnsubmitted();
            setTimesheetStatus(true);
            break;
        case "Approved":
            setTimesheetEntryStatus(updatedTimesheetData, "approved");
            props.setTimesheetStatusMessage("Timesheet Approved :)");
            disableAddTimecode();
            markTimesheetSubmitted();
            setTimesheetStatus(false);
            break;
        default:
            props.setTimesheetStatusMessage("");
            enableAddTimecode();
            markTimesheetUnsubmitted();
            setTimesheetStatus(true);
        }
        props.setTimesheetData(updatedTimesheetData);
        endLoad();
      };

    

    const handleSubmit = async () => {
        startLoad();
        let submitted = false;
        try {
            submitted = await SubmitTimesheet(currentWeekStart);
            showSuccessToast(dispatchToast, "Successfully submitted timesheet");
        } catch {
            showErrorToast(dispatchToast, "Failed to submit timesheet");
        }

        if (submitted) {
            // Update each day's status to "closed"
            const updatedTimesheetData = [...props.timesheetData];
            updatedTimesheetData.forEach(client => {
                client.projects.forEach(project => {
                    project.budgets.forEach(budget => {
                        budget.timecodes.forEach(timecode => {
                            timecode.entryDays.forEach(day => {
                                day.status = "closed";
                            });
                        });
                    });
                });
            });

            props.setTimesheetData(updatedTimesheetData);
            disableAddTimecode(true);
            markTimesheetSubmitted();
            props.setTimesheetStatusMessage("Timesheet Submitted");
        }
        endLoad();
    };

    return (        
        <div className="navigation-controls">
            {isDesktop? 
            (
                <>
                <div className="toolbar">
                    <div className="leftSection">
                    <div className="navButtons">
                        <TooltipWatcherWidget text="This is a button to cycle week" direction="above-start">
                            <button
                            type="button"
                            className={styles.buttonSecondary}
                            onClick={() => handleDateChange("previous")}
                            aria-label="Previous week"
                            >
                            <ChevronLeftRegular className={styles.icon24} />
                            </button>
                        </TooltipWatcherWidget>
                        <button
                        type="button"
                        className={styles.buttonSecondary}
                        onClick={() => handleDateChange("next")}
                        aria-label="Next week"
                        >
                        <ChevronRightRegular className={styles.icon24} />
                        </button>
                    </div>

                    <div className="weekText">
                        Week of {currentWeekStartDisplay()}
                    </div>
                    <div className="weekText">
                        <TooltipWatcherWidget text="This is a button to select a week" direction="above-start">
                            <Field>
                                <DatePicker 
                                className="datepicker"
                                placeholder=""
                                value={currentWeekStart}
                                calendar={{
                                    dateRangeType: DateRangeType.Week,
                                    firstDayOfWeek: DayOfWeek.Monday,
                                    showWeekNumbers: true
                                }}
                                style={{ 
                                    border: 'none', 
                                    width: '32px',
                                    height: '32px', 
                                    padding: 0,
                                    marginLeft: -10,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative',
                                    backgroundColor: "#f5f7f9",
                                    cursor: 'pointer'
                                }}
                                input={{
                                    style: { 
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%', 
                                        height: '100%', 
                                        opacity: 0, 
                                        cursor: 'pointer',
                                        margin: 0,
                                        padding: 0
                                    }
                                }}
                                onSelectDate={(date) => {
                                    let newWeekStart = previousMonday(date.setDate(date.getDate()+1))
                                    let lastWeekStart = previousMonday(newWeekStart);
                                    setCurrentWeekStart(newWeekStart); 
                                    setLastWeekStart(newWeekStart); 
                                    handleDateChange("",newWeekStart);
                                    document.activeElement.blur()}}
                                />
                            </Field>
                        </TooltipWatcherWidget>
                    </div>
                    </div>

                    <div className="rightSection">
                    <div className="statusMessage">
                        {props.timesheetStatusMessage}
                    </div>

                    <div>
                        <Tooltip withArrow
                        positioning="before"
                        relationship="description"
                        content={{children: "Click to toggle help", className: styles.tooltip}}>
                        {infoActive ? <Info32Filled className="tooltip-filled" onClick={handleInfoToggle}></Info32Filled> : <Info32Regular className="tooltip" onClick={handleInfoToggle}/>}
                        </Tooltip>
                    </div>
                    { !loading && (currentMonthEnd.getDate() < currentWeekStart.getDate() + 7) && !isCasualSubmitLocked && <CasualSubmit onSubmit={handleDateChange}/>}
                    <div className="submitButtonWrapper">
                        <Dialog open={open} onOpenChange={(e, { open }) => setOpen(open)}>
                        <DialogTrigger disableButtonEnhancement>
                            <TooltipWatcherWidget text="Submit your timesheet weekly" direction="above-end">
                                <ToolbarButton
                                className={styles.buttonPrimary}
                                onClick={() => setOpen(true)}
                                disabled={isTimesheetSubmitted}
                                >
                                Submit Timesheet
                                </ToolbarButton>
                            </TooltipWatcherWidget>
                        </DialogTrigger>

                        <DialogSurface>
                            <DialogBody>
                            <DialogTitle>Confirmation</DialogTitle>
                            <DialogContent>Are you sure you want to submit?</DialogContent>
                            <DialogActions>
                                <Button
                                className={styles.buttonPrimary}
                                onClick={() => {
                                    handleSubmit();
                                    setOpen(false);
                                }}
                                >
                                Yes
                                </Button>
                                <Button appearance="secondary" onClick={() => setOpen(false)}>
                                No
                                </Button>
                            </DialogActions>
                            </DialogBody>
                        </DialogSurface>
                        </Dialog>
                    </div>
                    {process.env.REACT_APP_ENVIRONMENT !== "prod" && <EnvConfig></EnvConfig>}
                    </div>
                </div>
                </>

            ) : (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: '12px',
                    width: '100%',
                    padding: '8px',}}>
                        <div className="weekText" style={{display: 'flex', justifyContent:'center', alignContent: 'center', gap: '15px'}}>
                        <div style={{flex: 1}}></div>
                        <div>
                            Week of <div style={{paddingTop: '5px'}}>{currentWeekStartDisplay().split(" ")[0]} {currentWeekStartDisplay().split(" ")[1].slice(0,3)} - {currentWeekStartDisplay().split(" ")[3]} {currentWeekStartDisplay().split(" ")[4].slice(0,3)} </div>
                        </div>
                        <div className="weekText" style={{flex: 1}}>
                            <Field>
                                <DatePicker 
                                className="datepicker"
                                placeholder=""
                                value={currentWeekStart}
                                calendar={{
                                    dateRangeType: DateRangeType.Week,
                                    firstDayOfWeek: DayOfWeek.Monday,
                                    showWeekNumbers: true
                                }}
                                style={{ 
                                    border: 'none', 
                                    width: '32px',
                                    height: '32px', 
                                    padding: 0,
                                    marginLeft: -10,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative',
                                    backgroundColor: "#f5f7f9",
                                    cursor: 'pointer'
                                }}
                                input={{
                                    style: { 
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%', 
                                        height: '100%', 
                                        opacity: 0, 
                                        cursor: 'pointer',
                                        margin: 0,
                                        padding: 0
                                    }
                                }}
                                onSelectDate={(date) => {
                                    let newWeekStart = previousMonday(date.setDate(date.getDate()+1))
                                    setCurrentWeekStart(newWeekStart); 
                                    handleDateChange("",newWeekStart);
                                    document.activeElement.blur()}}
                                />
                            </Field>
                        </div>
                        </div>
                
                    {/* Buttons Row */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '8px',
                        flexWrap: 'wrap',
                        width: '100%',}}>

                        <button
                            type="button"
                            className={styles.buttonSecondary}
                            onClick={() => handleDateChange("previous")}
                            aria-label="Previous week"
                            >
                            <ChevronLeftRegular className={styles.icon24} />
                            </button>
                
                        <Dialog open={open} onOpenChange={(e, { open }) => setOpen(open)}>
                            <DialogTrigger disableButtonEnhancement>
                                <ToolbarButton 
                                    className={styles.buttonPrimary}
                                onClick={() => setOpen(true)}
                                disabled={isTimesheetSubmitted}
                                >
                                    Submit
                                </ToolbarButton>
                            </DialogTrigger>
                
                            <DialogSurface>
                                <DialogBody>
                                    <DialogTitle>Confirmation</DialogTitle>
                                    <DialogContent>Are you sure you want to submit?</DialogContent>
                                    <DialogActions>
                                        <Button appearance="primary" onClick={() => { handleSubmit(); setOpen(false); }}>Yes</Button>
                                        <Button appearance="secondary" onClick={() => setOpen(false)}>No</Button>
                                    </DialogActions>
                                </DialogBody>
                            </DialogSurface>
                        </Dialog>

                        <button
                        type="button"
                        className={styles.buttonSecondary}
                        onClick={() => handleDateChange("next")}
                        aria-label="Next week"
                        >
                        <ChevronRightRegular className={styles.icon24} />
                        </button>
                    </div>
                
                    {/* Week and Start Info Side by Side */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '12px',
                        paddingLeft: '4px',
                        paddingRight: '4px',}}>
                            
                        
                    </div>
                    {props.timesheetStatusMessage && 
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '-1rem', color: 'black'}}>
                            <Body1Stronger>{props.timesheetStatusMessage}</Body1Stronger>
                    </div>}
                    {(currentMonthEnd.getDate() < currentWeekStart.getDate() + 6) && !isCasualSubmitLocked && <CasualSubmit onSubmit={handleDateChange}/>}
                </div>
            )}
        </div>
    );
}